import React from 'react';
import { Button, Divider, HelpText } from 'src/components/atoms';
import {
  StyledIcon,
  StyledIconInButton,
} from 'src/components/foundations/foundations.styles';
import { useModalRefs } from 'src/hooks/useModalRefs';
import { useTheme } from 'styled-components';
import { ModalActionBarHelpText } from 'src/types';
import { SpinnerButton } from '../SpinnerButton';
import {
  Bottom,
  BottomActionBar,
  BottomMessageContainer,
  ButtonContainer,
  ContentContainer,
  Description,
  IconButton,
  InvisibleWedge,
  ModalSize,
  ModalStyled,
  Title,
  TitleContainer,
} from './Modal.styles';

interface Props {
  size: ModalSize;
  title: string;
  description?: string | React.ReactNode;
  open: boolean;
  cancelButtonText?: string;
  confirmButtonText?: string;
  confirmButtonIcon?: { position: 'left' | 'right'; className: string };
  children?: React.ReactNode;
  setOpen: (open: boolean) => void;
  onCancel?: () => void;
  onConfirm?: () => void;
  'data-testid'?: string;
  hideButtons?: boolean;
  isConfirmButtonDisabled?: boolean;
  includeClose?: boolean;
  actionBarHelpText?: ModalActionBarHelpText;
  loading?: boolean;
  confirmButtonVariant?: 'primary' | 'secondary' | 'tertiary' | 'error';
  alternativeSecondaryButton?: React.ReactNode;
  closeOnOutsideClick?: boolean;
  callCancelOnOutsideClick?: boolean;
}

export const Modal = ({
  size,
  title,
  description,
  open,
  setOpen,
  onCancel,
  onConfirm,
  cancelButtonText = 'Cancel',
  confirmButtonText = 'Confirm',
  confirmButtonIcon,
  children,
  isConfirmButtonDisabled = false,
  includeClose = false,
  actionBarHelpText,
  loading = false,
  confirmButtonVariant = 'primary',
  alternativeSecondaryButton,
  closeOnOutsideClick = true,
  callCancelOnOutsideClick,
  ...props
}: Props) => {
  const { dialogRef, contentRef } = useModalRefs({
    closeModal: () => {
      if (callCancelOnOutsideClick && onCancel) {
        onCancel();
      }
      setOpen(false);
    },
    isOpen: open,
    closeOnOutsideClick,
  });
  const theme = useTheme();

  const closeModal = (e: React.KeyboardEvent<HTMLDialogElement>) => {
    if (onCancel) {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <ModalStyled
      show={open}
      size={size}
      ref={dialogRef}
      data-testid={props['data-testid']}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          closeModal(e);
        }
      }}
    >
      <ContentContainer ref={contentRef}>
        <TitleContainer hasPaddingBottom={Boolean(props.hideButtons)}>
          {props.hideButtons && (
            <IconButton>
              <StyledIcon
                size="24px"
                className="uil uil-times"
                onClick={onCancel}
                data-testid={`${props['data-testid']}-close`}
              />
            </IconButton>
          )}
          <Title
            hasLargeText={Boolean(props.hideButtons)}
            hasContent={Boolean(description ?? children)}
          >
            {title}
          </Title>
          {includeClose && (
            <IconButton>
              <StyledIcon
                size="24px"
                className="uil uil-times"
                onClick={onCancel}
                data-testid={`${props['data-testid']}-close`}
              />
            </IconButton>
          )}
          {!includeClose && <InvisibleWedge />}
        </TitleContainer>
        {description && <Description>{description}</Description>}
        {children}

        {!props.hideButtons && (
          <Bottom>
            <Divider
              variant="horizontal"
              color={theme.atlas.semantic.color.ui.divider.default}
            />
            <BottomActionBar>
              <BottomMessageContainer>
                {actionBarHelpText && (
                  <HelpText
                    state={actionBarHelpText.messageState}
                    title={actionBarHelpText.title}
                    helpText={actionBarHelpText.text}
                    data-testid="action-bar-message"
                  />
                )}
              </BottomMessageContainer>
              <ButtonContainer>
                {onCancel && !alternativeSecondaryButton && (
                  <Button
                    variant="secondary"
                    onClick={onCancel}
                    data-testid={`${props['data-testid']}-cancel`}
                  >
                    {cancelButtonText}
                  </Button>
                )}
                {alternativeSecondaryButton}
                <SpinnerButton
                  onClick={onConfirm}
                  data-testid={`${props['data-testid']}-confirm`}
                  disabled={isConfirmButtonDisabled}
                  loading={loading}
                  variant={confirmButtonVariant}
                >
                  {confirmButtonIcon &&
                    confirmButtonIcon.position === 'left' && (
                      <StyledIconInButton
                        className={confirmButtonIcon.className}
                        size="medium"
                        position="left"
                      />
                    )}
                  {confirmButtonText}
                  {confirmButtonIcon &&
                    confirmButtonIcon.position === 'right' && (
                      <StyledIconInButton
                        className={confirmButtonIcon.className}
                        size="medium"
                        position="right"
                      />
                    )}
                </SpinnerButton>
              </ButtonContainer>
            </BottomActionBar>
          </Bottom>
        )}
      </ContentContainer>
    </ModalStyled>
  );
};
